import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import {
  Link as LinkIcon, YoutubeLogo, Playlist, ListPlus, X, Stack, Warning,
  CheckCircle, Copy as CopyIcon, StopCircle, Sparkle, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const KIND_TABS = [
  { key: "urls", label: "Paste URLs", icon: ListPlus },
  { key: "youtube_channel", label: "YouTube channel", icon: YoutubeLogo },
  { key: "youtube_playlist", label: "YouTube playlist", icon: Playlist },
];

const STATUS_COLOR = {
  running: "bg-amber-50 border-amber-300 text-amber-800",
  done: "bg-emerald-50 border-emerald-300 text-emerald-800",
  cancelled: "bg-stone-100 border-stone-300 text-stone-700",
  failed: "bg-rose-50 border-rose-300 text-rose-800",
};

function JobCard({ job, onCancel, onRefresh }) {
  const pct = job.total ? Math.round((job.completed / job.total) * 100) : 0;
  const bar = STATUS_COLOR[job.status] || STATUS_COLOR.running;
  return (
    <div className={`rounded-xl border p-3 ${bar}`} data-testid={`bulk-job-${job.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase">
          {job.status}
        </span>
        <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase opacity-70">
          {job.kind.replace("_", " ")}
        </span>
        <span className="ml-auto font-mono-tech text-[10px] tracking-[0.22em]">
          {job.completed}/{job.total}
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full rounded-full bg-white/60 overflow-hidden">
        <div
          className="h-full rounded-full bg-current opacity-70 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono-tech tracking-widest uppercase">
        <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">
          <CheckCircle size={9} weight="fill" className="inline mr-0.5" />
          {job.published || 0} published
        </span>
        {(job.duplicated || 0) > 0 && (
          <span className="rounded-full bg-sky-100 text-sky-800 px-2 py-0.5">
            {job.duplicated} duplicate
          </span>
        )}
        {(job.blocked || 0) > 0 && (
          <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">
            {job.blocked} blocked
          </span>
        )}
        {(job.failed || 0) > 0 && (
          <span className="rounded-full bg-rose-100 text-rose-800 px-2 py-0.5">
            {job.failed} failed
          </span>
        )}
      </div>
      {job.current_url && job.status === "running" && (
        <div className="mt-2 text-[11px] opacity-80 truncate" title={job.current_url}>
          ▸ {job.current_url}
        </div>
      )}
      <div className="mt-2 flex gap-2 flex-wrap">
        {job.status === "running" && (
          <button
            onClick={() => onCancel(job.id)}
            data-testid={`bulk-cancel-${job.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-rose-400 bg-rose-50 text-rose-800 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-rose-100"
          >
            <StopCircle size={11} weight="fill" /> Cancel
          </button>
        )}
        <button
          onClick={() => onRefresh(job.id)}
          data-testid={`bulk-refresh-${job.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white text-[#151312] px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:bg-black/5"
        >
          Details
        </button>
      </div>
    </div>
  );
}

function JobDetailsModal({ job, onClose }) {
  if (!job) return null;
  const results = job.results || [];
  return (
    <div
      className="fixed inset-0 z-[70] bg-[#241209]/70 backdrop-blur-sm flex items-center justify-center px-3 py-6"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="bulk-details-modal"
    >
      <div className="bg-[#F5EBDC] rounded-2xl w-full max-w-2xl overflow-hidden border border-black/15 shadow-2xl">
        <div className="bg-[#2A1F14] text-[#E4A15C] px-5 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase opacity-80">
              Bulk import · {job.status}
            </div>
            <div className="font-serif-display text-base truncate">
              {job.total} URLs · {job.published || 0} published, {job.duplicated || 0} duplicate, {job.failed || 0} failed
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-[#E4A15C]/10">
            <X size={15} weight="bold" />
          </button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-1.5">
          {results.length === 0 && (
            <div className="text-center text-[#3A3830]/70 py-8">No per-URL results yet…</div>
          )}
          {results.map((r, i) => {
            const bg = {
              created: "bg-emerald-50 text-emerald-900 border-emerald-300",
              duplicate: "bg-sky-50 text-sky-900 border-sky-300",
              blocked: "bg-amber-50 text-amber-900 border-amber-300",
              failed: "bg-rose-50 text-rose-900 border-rose-300",
              cancelled: "bg-stone-50 text-stone-800 border-stone-300",
            }[r.status] || "bg-white text-[#151312] border-black/15";
            return (
              <div key={i} className={`rounded-lg border p-2 text-xs ${bg}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase font-bold">
                    {r.status}
                  </span>
                  {r.id && (
                    <a href={`/stories/${r.id}`} target="_blank" rel="noreferrer"
                       className="text-[#B85F2A] hover:underline">Open</a>
                  )}
                  <span className="ml-auto truncate opacity-70">{r.url}</span>
                </div>
                {r.title && <div className="mt-0.5 font-serif-display text-sm truncate">{r.title}</div>}
                {r.error && <div className="mt-0.5 text-[10px] opacity-80">{r.error}</div>}
                {r.message && <div className="mt-0.5 text-[10px] opacity-80">{r.message}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function BulkImportPanel() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("urls");
  const [urlText, setUrlText] = useState("");
  const [channelInput, setChannelInput] = useState("");
  const [playlistInput, setPlaylistInput] = useState("");
  const [depth, setDepth] = useState("standard");
  const [autopublish, setAutopublish] = useState(true);
  const [notes, setNotes] = useState("");
  const [maxItems, setMaxItems] = useState(25);
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);

  const parsedUrls = urlText.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^https?:\/\//.test(s));

  const loadJobs = async () => {
    try {
      const { data } = await http.get("/nature/sources/bulk-jobs", { params: { limit: 10 } });
      setJobs(data.jobs || []);
    } catch (_e) { /* not admin */ }
  };

  // Poll every 2s while any job is running
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => {
    if (!jobs.some((j) => j.status === "running")) return;
    const t = setInterval(loadJobs, 2000);
    return () => clearInterval(t);
  }, [jobs]);

  const start = async () => {
    setStarting(true);
    try {
      const body = { kind, depth, autopublish, notes: notes || null, max_items: maxItems };
      if (kind === "urls") body.urls = parsedUrls;
      if (kind === "youtube_channel") body.input = channelInput.trim();
      if (kind === "youtube_playlist") body.input = playlistInput.trim();
      const { data } = await http.post("/nature/sources/bulk-import", body, { timeout: 30000 });
      toast.success(`Bulk job started — processing ${data.total} URLs in the background`);
      // Optimistic clear
      if (kind === "urls") setUrlText("");
      if (kind === "youtube_channel") setChannelInput("");
      if (kind === "youtube_playlist") setPlaylistInput("");
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed to start");
    } finally { setStarting(false); }
  };

  const cancel = async (id) => {
    try {
      await http.post(`/nature/sources/bulk-jobs/${id}/cancel`);
      toast.message("Cancellation requested");
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const showDetails = async (id) => {
    try {
      const { data } = await http.get(`/nature/sources/bulk-jobs/${id}`);
      setOpenJob(data);
    } catch (e) {
      toast.error("Failed to load job details");
    }
  };

  const canStart =
    !starting &&
    ((kind === "urls" && parsedUrls.length > 0) ||
      (kind === "youtube_channel" && channelInput.trim()) ||
      (kind === "youtube_playlist" && playlistInput.trim()));

  return (
    <div className="rounded-2xl border border-black/15 bg-white overflow-hidden" data-testid="bulk-panel">
      <button
        onClick={() => setOpen(!open)}
        data-testid="bulk-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#FFF6E5] transition"
      >
        <Stack size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <div className="text-left flex-1">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Bulk Import
          </div>
          <div className="font-serif-display text-base text-[#151312]">
            Paste a list of URLs or import a whole YouTube channel / playlist
          </div>
        </div>
        {jobs.some((j) => j.status === "running") && (
          <span className="rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
            {jobs.filter((j) => j.status === "running").length} running
          </span>
        )}
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>

      {open && (
        <div className="border-t border-black/10 p-4 space-y-4">
          {/* Mode tabs */}
          <div className="flex flex-wrap gap-2">
            {KIND_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setKind(t.key)}
                data-testid={`bulk-kind-${t.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
                  kind === t.key
                    ? "bg-[#2A1F14] text-[#E4A15C] border-[#2A1F14]"
                    : "border-black/15 bg-white text-[#151312] hover:bg-black/5"
                }`}
              >
                <t.icon size={12} weight="fill" /> {t.label}
              </button>
            ))}
          </div>

          {/* Input area — one per mode */}
          {kind === "urls" && (
            <div>
              <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                Paste up to {maxItems} URLs — one per line or comma-separated
              </label>
              <textarea
                data-testid="bulk-urls-textarea"
                value={urlText}
                onChange={(e) => setUrlText(e.target.value)}
                rows={5}
                placeholder="https://youtu.be/…&#10;https://www.nature.com/…&#10;https://en.wikipedia.org/wiki/…"
                className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm font-mono outline-none focus:border-[color:var(--np-brand,#B85F2A)] resize-y"
              />
              <div className="mt-1 text-[10px] text-[#3A3830]/70 font-mono-tech tracking-widest uppercase">
                {parsedUrls.length} valid URL{parsedUrls.length === 1 ? "" : "s"} detected
              </div>
            </div>
          )}

          {kind === "youtube_channel" && (
            <div>
              <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                YouTube channel handle (e.g. <b>@FreeSchool</b>) or full URL
              </label>
              <input
                data-testid="bulk-channel-input"
                value={channelInput}
                onChange={(e) => setChannelInput(e.target.value)}
                placeholder="@FreeSchool  or  https://www.youtube.com/channel/UC…"
                className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
              />
              <div className="mt-1 text-[10px] text-[#3A3830]/70 font-mono-tech tracking-widest uppercase">
                Pulls the channel's latest {maxItems} videos via YouTube's public RSS feed
              </div>
            </div>
          )}

          {kind === "youtube_playlist" && (
            <div>
              <label className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]/80 block mb-1">
                YouTube playlist ID or full playlist URL
              </label>
              <input
                data-testid="bulk-playlist-input"
                value={playlistInput}
                onChange={(e) => setPlaylistInput(e.target.value)}
                placeholder="PLxxx…  or  https://www.youtube.com/playlist?list=PL…"
                className="w-full rounded-full border border-black/15 bg-white px-4 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
              />
            </div>
          )}

          {/* Settings row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <select
              data-testid="bulk-depth"
              value={depth}
              onChange={(e) => setDepth(e.target.value)}
              className="rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
            >
              <option value="quick">Quick summary</option>
              <option value="standard">Standard article</option>
              <option value="detailed">Detailed deep-dive</option>
            </select>
            <input
              type="number"
              min="1"
              max="100"
              value={maxItems}
              onChange={(e) => setMaxItems(Math.max(1, Math.min(100, +e.target.value || 25)))}
              data-testid="bulk-max"
              className="rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
              placeholder="Max items"
            />
            <label className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-3 py-2 text-sm">
              <input
                type="checkbox"
                data-testid="bulk-autopublish"
                checked={autopublish}
                onChange={(e) => setAutopublish(e.target.checked)}
                className="h-4 w-4"
              />
              Auto-publish
            </label>
            <button
              onClick={start}
              disabled={!canStart}
              data-testid="bulk-start"
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 border border-[#151312] bg-[#151312] text-[#E4A15C] font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#E4A15C] hover:text-[#151312] disabled:opacity-40"
            >
              <Sparkle size={12} weight="fill" /> {starting ? "Starting…" : "Start import"}
            </button>
          </div>

          <input
            data-testid="bulk-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional editor notes / angle applied to every article"
            className="w-full rounded-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[color:var(--np-brand,#B85F2A)]"
          />

          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-[11px] p-2 flex items-start gap-2">
            <Warning size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
            <div>
              Facebook / Instagram / TikTok URLs will be marked as <b>blocked</b> and skipped —
              they need a manual description. Add them via the single-URL panel above.
            </div>
          </div>

          {/* Active + recent jobs */}
          {jobs.length > 0 && (
            <div className="border-t border-black/10 pt-3 space-y-2">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#151312]">
                Recent bulk jobs
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {jobs.map((j) => (
                  <JobCard key={j.id} job={j} onCancel={cancel} onRefresh={showDetails} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {openJob && <JobDetailsModal job={openJob} onClose={() => setOpenJob(null)} />}
    </div>
  );
}
